/**
 * TrialBanner — pinned to the top of every authenticated page.
 *
 * Renders in two states:
 *   1. Active trial: shows days remaining + contact CTA. Color grades from
 *      indigo (>1 day) to amber (<=1 day) for soft urgency.
 *   2. Expired trial: shows "Your trial ended" + reactivation CTA. Always amber.
 *
 * Hidden entirely when the org is on a non-trial plan or when trial state
 * hasn't loaded yet.
 *
 * The CTA opens the existing SupportForm modal pre-filled with billing
 * context. SupportForm POSTs to /api/support/request which emails the
 * founders via Azure Communication Services — the same path the regular
 * Contact Support flow uses. We rejected the mailto:/clipboard variants
 * because they don't actually contact anyone unless the user takes
 * additional manual steps; this gives a structured request that hits the
 * founder inbox directly.
 */

import React, { useState } from 'react';
import { useBilling } from '../../contexts/BillingContext';
import SupportForm from '../support/SupportForm';

const TrialBanner = () => {
  const { isTrialActive, isTrialExpired, trialDaysRemaining } = useBilling();
  const [supportOpen, setSupportOpen] = useState(false);

  if (!isTrialActive && !isTrialExpired) {
    return null;
  }

  // Pre-fill context the user can edit before sending. Title makes the
  // founder inbox subject clear; description seeds the body with a sensible
  // starting point. Type=billing routes the email with a "BILLING / TRIAL"
  // prefix per routes/support.py request_type_mapping.
  const trialInitialValues = isTrialExpired
    ? {
        requestType: 'billing',
        title: 'Sparvi trial ended — reactivate the full plan',
        description:
          "My trial just ended and I'd like to reactivate the Sparvi plan. " +
          'Please let me know the next step for billing.',
        priority: 'high',
      }
    : {
        requestType: 'billing',
        title: 'Sparvi trial — talk to us about next steps',
        description:
          "I'm on the Sparvi trial and would like to talk about " +
          "what comes next. A few questions / things I'm trying to figure out:\n\n",
        priority: 'medium',
      };

  if (isTrialExpired) {
    return (
      <>
        <div className="w-full bg-amber-50 border-b border-amber-200 px-4 sm:px-6 py-2.5">
          <div className="max-w-7xl mx-auto flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2">
            <p className="text-sm text-amber-900">
              <span className="font-semibold">Your Sparvi trial has ended.</span>{' '}
              You&apos;re on the Free plan with limited features. Reach out to
              keep going on the full Sparvi plan.
            </p>
            <button
              type="button"
              onClick={() => setSupportOpen(true)}
              className="text-sm font-medium text-amber-900 underline hover:text-amber-700 whitespace-nowrap focus:outline-none focus:ring-2 focus:ring-amber-500 rounded"
            >
              Contact us to upgrade
            </button>
          </div>
        </div>
        <SupportForm
          isOpen={supportOpen}
          onClose={() => setSupportOpen(false)}
          initialValues={trialInitialValues}
          modalTitle="Reactivate Sparvi"
        />
      </>
    );
  }

  // Active trial. Below 2 days, swap to amber for soft urgency.
  const urgent = trialDaysRemaining !== null && trialDaysRemaining <= 1;
  const bg = urgent ? 'bg-amber-50 border-amber-200' : 'bg-indigo-50 border-indigo-200';
  const text = urgent ? 'text-amber-900' : 'text-indigo-900';
  const link = urgent
    ? 'text-amber-900 hover:text-amber-700'
    : 'text-indigo-700 hover:text-indigo-900';

  const daysLabel =
    trialDaysRemaining === null
      ? 'Your Sparvi trial is active'
      : trialDaysRemaining === 1
        ? '1 day left in your Sparvi trial'
        : `${trialDaysRemaining} days left in your Sparvi trial`;

  return (
    <>
      <div className={`w-full border-b px-4 sm:px-6 py-2.5 ${bg}`}>
        <div className="max-w-7xl mx-auto flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2">
          <p className={`text-sm ${text}`}>
            <span className="font-semibold">{daysLabel}.</span>{' '}
            Want to keep everything running after the trial? Talk to us.
          </p>
          <button
            type="button"
            onClick={() => setSupportOpen(true)}
            className={`text-sm font-medium underline whitespace-nowrap focus:outline-none focus:ring-2 focus:ring-offset-2 rounded ${link}`}
          >
            Talk to us
          </button>
        </div>
      </div>
      <SupportForm
        isOpen={supportOpen}
        onClose={() => setSupportOpen(false)}
        initialValues={trialInitialValues}
        modalTitle="Talk to us about your trial"
      />
    </>
  );
};

export default TrialBanner;
